import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getMetadataOptions } from "../nfts/getMetadata";
import type { ApiError } from "../types";
import { purgeCdnCache, purgeNFTAlchemyCache } from "./nft-cache";

const API_URL = import.meta.env.VITE_API_URL;

// Types for Metadata
export interface NFTMetadata {
	tokenId: number;
	name: string;
	description?: string;
	displayWeight: number;
	image?: string;
	attributes?: Record<string, string>[];
}

interface UploadMetadataDto {
	collectionAddress: string;
	metadata: NFTMetadata[];
}

interface BatchUploadResponseDto {
	totalUploaded: number;
	uploadQueueIds: number[];
}

const uploadMetadata = async (data: UploadMetadataDto) => {
	const response = await fetch(`${API_URL}/inventory/metadata/json`, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const error = await response.json();
		throw error;
	}

	return (await response.json()).data;
};

export const useUploadMetadataJsonMutation = () => {
	const queryClient = useQueryClient();
	return useMutation<BatchUploadResponseDto, ApiError, UploadMetadataDto>({
		mutationFn: uploadMetadata,
		onSuccess: async (_, variables) => {
			await purgeCdnCache({
				keys: variables.metadata.map(
					(metadata) =>
						`${variables.collectionAddress}/${metadata.tokenId.toString()}.json`,
				),
			});
			await purgeNFTAlchemyCache({
				collectionAddress: variables.collectionAddress,
				tokenIds: variables.metadata.map((metadata) => metadata.tokenId),
			});
			await Promise.all(
				variables.metadata.map(async (metadata) => {
					await queryClient.invalidateQueries(
						getMetadataOptions(
							variables.collectionAddress,
							metadata.tokenId.toString(),
						),
					);
				}),
			);
			toast.success("Metadata uploaded successfully");
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});
};

export const useUploadMetadataFilesMutation = () => {
	const queryClient = useQueryClient();
	return useMutation<
		BatchUploadResponseDto,
		ApiError,
		{ files: File[]; collectionAddress: string }
	>({
		mutationFn: async ({ files, collectionAddress }) => {
			// Process each JSON file
			const metadataPromises = files.map(async (file) => {
				// Extract tokenId from filename (e.g., "1.json" -> 1)
				const tokenId = Number(file.name.replace(".json", ""));
				if (Number.isNaN(tokenId)) {
					throw new Error(`Invalid filename: ${file.name}`);
				}

				// Read and parse the JSON file
				const text = await file.text();
				let metadata: NFTMetadata;
				try {
					metadata = JSON.parse(text);
				} catch (e: unknown) {
					const error = e instanceof Error ? e : new Error(String(e));
					throw new Error(
						`Invalid JSON in file ${file.name}: ${error.message}`,
					);
				}

				// Override the tokenId from the filename
				return {
					...metadata,
					tokenId,
				};
			});

			const metadataArray = await Promise.all(metadataPromises);

			return uploadMetadata({
				collectionAddress,
				metadata: metadataArray,
			});
		},
		onSuccess: async (_, variables) => {
			await queryClient.invalidateQueries({
				queryKey: ["nfts", "metadata", variables.collectionAddress],
			});
			toast.success("Metadata updated successfully");
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});
};
